import React, { useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import {
  Button,
  Checkbox,
  Dropdown,
  FormField,
  Icon,
  Link,
  Typography,
} from 'components/alf-design';
import MakePayment from 'components/dashboard/sections/Billing/MakePayment';
import { useForm } from 'utils';
import getMinAmountDue from 'utils/getMinAmountDue';
import formats, { capitalize } from 'utils/formats';
import { warning } from 'assets/icon';
import { LegacyColors } from 'constants';

const options = [
  { label: 'Credit Card', value: 'CreditCard' },
  { label: 'Checking Account', value: 'ECheck' },
  { label: 'Savings Account', value: 'ECheck' },
];

const Summary = ({ data }) => {
  const billing = data?.billing;
  const hasReinstateAmountDue =
    billing?.allowPolicyReinstate === 1 && billing?.policyReinstateAmount > 0;
  const hasStandardBalanceDue =
    (billing?.currentDueAmount ?? 0) > 0 ||
    (billing?.outstandingBalance ?? 0) > 0;
  const continueDisabled = !hasReinstateAmountDue && !hasStandardBalanceDue;

  const [selected, setSelected] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [errors, setErrors] = useState(null);
  const [minAmount, setMinAmount] = useState(null);
  const [showPayment, togglePayment] = useState(false);
  const { handleChange, values, touched, setFormValues } = useForm({
    otherAmount: undefined,
  });
  const selectedPolicy = useMemo(
    () => ({
      ...data,
      isFusion: Boolean(data?.lineType),
      minAmountDue: minAmount,
      insured: {
        firstName: data?.insuredFirstName,
        lastName: data?.insuredLastName,
        zip: data?.insuredZip,
        street: data?.insuredStreet,
        city: data?.insuredCity,
        state: data?.insuredState,
      },
      paymentCategory: paymentType?.value,
    }),
    [data, minAmount, paymentType],
  );

  const handleCheckbox = (e) => {
    if (selected !== e.target.name) {
      handleChange(e);
      setSelected(e.target.name);
      setErrors(null);
    }
  };

  const handleInputChange = (e) => {
    e.target.value = e.target.value.replace(/[^0-9.]/g, '');
    setSelected('otherAmount');
    setErrors(null);
    handleChange(e);
  };

  const handleContinue = async () => {
    if (selected === null) {
      return setErrors({
        selected: 'Please select a payment amount.',
      });
    }

    if (selected === 'otherAmount') {
      if (values?.otherAmount === '' || values?.otherAmount === undefined) {
        return setErrors({ otherAmount: 'Other Amount Required' });
      } else {
        if (values?.otherAmount < data?.billing?.currentDueAmount) {
          return setErrors({
            otherAmount:
              'Payment amount cannot be less than the minimum amount due.',
          });
        }
        if (values?.otherAmount > data?.billing?.outstandingBalance) {
          return setErrors({
            otherAmount:
              'Payment amount cannot be greater than the remaining balance.',
          });
        }
        setErrors(null);
      }
    }

    if (paymentType === null) {
      return setErrors({
        paymentType: 'Please select a payment type.',
      });
    }

    if (errors === null) {
      togglePayment(true);
    }
  };

  useEffect(() => {
    if (selected === 'remainingBalance') {
      setFormValues({ otherAmount: '' }, false);
      setErrors(null);
      setMinAmount(data?.billing?.outstandingBalance);
    }
    if (selected === 'amountDue') {
      setFormValues({ otherAmount: '' }, false);
      setErrors(null);
      setMinAmount(data?.billing?.currentDueAmount);
    }
    if (selected === 'otherAmount') {
      setMinAmount(values?.otherAmount);
    }
    if (selected === 'reinstateAmount') {
      setFormValues({ otherAmount: '' }, false);
      setErrors(null);
      setMinAmount(getMinAmountDue(billing));
    }
  }, [selected, values?.otherAmount, billing]);

  return (
    <div className="grid gap-[20px]">
      {data?.billing.allowPolicyReinstate === 1 ? (
        <div className="flex gap-[12px] bg-legacy-warningBg border border-legacy-warningBorder p-[13px] rounded-[8px]">
          <img alt="warnin-icon" src={warning} className="w-[24px] h-[24px]" />
          <Typography component="span" className="!text-legacy-warning">
            This policy has been cancelled due to non-payment. To reinstate your
            policy, please pay the amount shown below.
          </Typography>
        </div>
      ) : (
        (data?.billing?.currentDueAmount === 0 ||
          data?.billing?.currentDueAmount === null) &&
        (data?.billing?.outstandingBalance === 0 ||
          data?.billing?.outstandingBalance === null) && (
          <div className="flex bg-legacy-bgSuccess p-[15px]">
            <Icon name="checkCircleFull" color={LegacyColors.successIcon} />
            <Typography className="w-[90%] !text-legacy-success ml-[15px]">
              Your policy shows no remaining balance. No payment is needed at
              this time. If you have questions, contact Slide Customer Service.
            </Typography>
          </div>
        )
      )}
      <div className="grid md:flex gap-[20px]">
        <div className="md:flex-1">
          <Typography type="subtitle" className="lg:!text-[18px]">
            Policy Number:
          </Typography>
          <Typography type="subtitle" className="!font-normal">
            {data?.policyNumber}
          </Typography>
        </div>
        <div className="md:flex-1">
          <Typography type="subtitle" className="lg:!text-[18px]">
            Policyholder Name:
          </Typography>
          <Typography
            type="subtitle"
            className="!font-normal"
          >{`${capitalize(data?.insuredFirstName)} ${capitalize(data?.insuredLastName)}`}</Typography>
        </div>
      </div>
      <div>
        <Typography type="subtitle" className="lg:!text-[18px]">
          Insured Property:
        </Typography>
        <Typography type="subtitle" className="!font-normal">
          {data?.insuredStreet}
        </Typography>
        <Typography
          type="subtitle"
          className="!font-normal"
        >{`${data?.insuredCity}, ${
          data?.insuredState
        } ${formats.zipFormat(data?.insuredZip)}`}</Typography>
      </div>
      <hr className="h-0 w-[190px] md:w-[420px] ml-0"></hr>
      <div className="flex items-start justify-start gap-[12px] w-fit max-w-[100%]">
        <div className="flex flex-col w-fit max-w-[100%] gap-[12px]">
          {data?.billing.allowPolicyReinstate === 1 ? (
            <Checkbox
              id="reinstateAmount"
              name="reinstateAmount"
              className="text-[14px] lg:text-[18px]"
              checked={selected === 'reinstateAmount'}
              onChange={handleCheckbox}
              error={null}
              disabled={
                data?.billing?.policyReinstateAmount === 0 ||
                data?.billing?.policyReinstateAmount === null
              }
            >
              <Typography
                className="grid md:flex md:items-center pl-[35px] mb-[12px]"
                type="caption1"
              >
                Reinstatement Amount Due:{' '}
                <b className="text-[12px] lg:text-[14px] md:ml-[10px]">
                  {data?.billing
                    ? formats.moneyFormat(data?.billing?.policyReinstateAmount)
                    : '$0.00'}
                </b>
              </Typography>
            </Checkbox>
          ) : (
            <>
              <Checkbox
                id="remainingBalance"
                name="remainingBalance"
                className="checkbox"
                checked={selected === 'remainingBalance'}
                onChange={handleCheckbox}
                error={null}
                disabled={
                  data?.billing?.outstandingBalance === 0 ||
                  data?.billing?.outstandingBalance === null
                }
              >
                <Typography
                  className="grid md:flex md:items-center pl-[35px] mb-[12px]"
                  type="caption1"
                >
                  Remaining Balance:{' '}
                  <b className="text-[12px] lg:text-[14px] md:ml-[10px]">
                    {data?.billing
                      ? formats.moneyFormat(data?.billing?.outstandingBalance)
                      : '$0.00'}
                  </b>
                </Typography>
              </Checkbox>
              <Checkbox
                id="amountDue"
                name="amountDue"
                className="checkbox"
                checked={selected === 'amountDue'}
                onChange={handleCheckbox}
                error={null}
                disabled={
                  data?.billing?.currentDueAmount === 0 ||
                  data?.billing?.currentDueAmount === null
                }
              >
                <Typography
                  className="grid md:flex md:items-center pl-[35px] mb-[12px]"
                  type="caption1"
                >
                  Minimum Amount Due:{' '}
                  <b className="text-[12px] lg:text-[14px] md:ml-[10px]">
                    {data?.billing
                      ? formats.moneyFormat(data?.billing?.currentDueAmount)
                      : '$0.00'}
                  </b>
                </Typography>
              </Checkbox>
              <Checkbox
                id="otherAmount"
                name="otherAmount"
                className="checkbox otherAmount"
                checked={selected === 'otherAmount'}
                onChange={handleCheckbox}
                error={null}
              >
                <div className="md:flex pl-[35px] mb-[12px]">
                  <Typography className="mr-[10px]" type="caption1">
                    Other Amount:{' '}
                  </Typography>
                  <FormField
                    label=""
                    error={touched?.otherAmount ? errors?.otherAmount : ''}
                    fullWidth={false}
                  >
                    <NumericFormat
                      id="otherAmountInput"
                      data-testid="otherAmountInput"
                      name="otherAmount"
                      onChange={handleInputChange}
                      thousandSeparator
                      prefix="$"
                      value={values?.otherAmount}
                      className="!relative !h-[36px] !w-[150px] bg-legacy-bgPurple !border-none !rounded-none pl-[10px] mt-0 md:mt-[-8px] mb-[8px]"
                    />
                  </FormField>
                </div>
              </Checkbox>
            </>
          )}
        </div>
        {errors?.selected && (
          <Typography
            type="error"
            component="span"
            className="inline-flex items-center whitespace-nowrap ml-0"
          >
            {errors?.selected}
          </Typography>
        )}
      </div>
      <div className="grid md:flex md:items-center">
        <Typography type="subtitle" className="lg:!text-[18px]">
          Payment Type:
        </Typography>
        <div className="inline-flex items-center gap-[12px]">
          <Dropdown
            label="Select payment type"
            options={options}
            className="ml-[10px]"
            onSelect={setPaymentType}
            error={null}
            setError={setErrors}
          />
          {errors?.paymentType && (
            <Typography
              type="error"
              component="span"
              className="inline-flex items-center whitespace-nowrap ml-0"
            >
              {errors?.paymentType}
            </Typography>
          )}
        </div>
      </div>
      <div>
        <Typography type="subtitle" className="!font-normal">
          The Other amount field may be used to enter an alternate payment
          amount. An amount less than the Minimum Amount Due or greater than the
          Remaining balance will not be accepted. If this policy is on an
          installment plan, entering an alternate payment amount will result in
          an adjustment to future installment amounts.
        </Typography>
      </div>
      <div>
        <Typography type="subtitle" className="!font-normal">
          This transaction is payment for an insurance policy. There are no
          returns or refunds. Please refer to the policy for terms and
          conditions concerning cancellation and refund of unearned premium. If
          this transaction is for the purchase of a new insurance policy, the
          policy declarations will be mailed within three business days.
        </Typography>
      </div>
      <Typography type="subtitle" className="!text-[16px] !font-medium">
        If there are questions regarding this payment or if technical
        difficulties occur, please contact Slide Insurance Company Customer
        Service{' '}
        <Link
          data-testid="customer-service"
          data-dd-action-name="payment.customer_service.call"
          href={`tel:800-748-2030`}
          className="!text-legacy-secondary font-black"
          tabIndex="0"
        >
          (800) 748-2030.
        </Link>
      </Typography>
      <div className="text-center mt-[10px]">
        <Button
          id="continue-login"
          data-dd-action-name="payment.summary.continue"
          type="brand"
          onClick={handleContinue}
          className="min-w-[120px] md:min-w-[320px]"
          disabled={continueDisabled}
        >
          Continue
        </Button>
      </div>

      <div id="portalOneContainer"></div>
      <MakePayment
        show={showPayment}
        onClose={() => togglePayment(false)}
        selectedPolicy={selectedPolicy}
        oneTimePayment={true}
      />
    </div>
  );
};

export default Summary;
